import { useState, useEffect } from 'react'

const BULLETS = [
  'One call with a solutions engineer. Zero sales pitch.',
  'We dig into your payment rails and compliance posture together.',
  'You leave with clear next steps and a written follow-up.',
]

export default function CTASection() {
  const [showHubSpotEmbed, setShowHubSpotEmbed] = useState(false)
  const HUBSPOT_MEETING_URL = process.env.NEXT_PUBLIC_HUBSPOTAPI_KEY

  const utmParams = {
    ref: 'secure-flow-cta',
    utm_source: 'website',
    utm_medium: 'cta',
    utm_campaign: 'secure_flow',
    utm_content: 'secure_flow__cta__book_a_demo',
  }

  const addUtmToUrl = (url) => {
    if (!url) return url
    const params = new URLSearchParams(utmParams)
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}${params.toString()}`
  }

  const openHubSpotBooking = (e) => {
    if (e?.preventDefault) e.preventDefault()
    setShowHubSpotEmbed(true)
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href)
      Object.entries(utmParams).forEach(([key, value]) => {
        currentUrl.searchParams.set(key, value)
      })
      window.history.replaceState({}, '', currentUrl.pathname + '?' + currentUrl.searchParams.toString())
    }
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden'
    }
  }

  const closeHubSpotBooking = () => {
    setShowHubSpotEmbed(false)
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href)
      Object.keys(utmParams).forEach((key) => {
        currentUrl.searchParams.delete(key)
      })
      const newQuery = currentUrl.searchParams.toString()
      window.history.replaceState({}, '', currentUrl.pathname + (newQuery ? `?${newQuery}` : ''))
    }
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'unset'
    }
  }

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showHubSpotEmbed) closeHubSpotBooking()
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleEscape)
      return () => window.removeEventListener('keydown', handleEscape)
    }
  }, [showHubSpotEmbed])

  return (
    <section className="sf-cta-section" id="demo">
      <div className="sf-cta-grid">
        <div className="sf-cta-left sf-reveal" data-sf-reveal>
          <span className="sf-eyebrow"><span className="sf-pulse" />Get on a call</span>
          <h2 className="sf-cta-h2">
            Book a demo with our <em>solutions team.</em>
          </h2>
          <p className="sf-cta-sub">
            Watch SecureFlow run live, tour the architecture, and put your questions in front of
            someone who can actually answer them.
          </p>
          <ol className="sf-cta-bullets">
            {BULLETS.map((b, i) => (
              <li key={i}>
                <span className="sf-cta-num">{i + 1}</span>
                <span className="sf-cta-txt">{b}</span>
              </li>
            ))}
          </ol>
          <div className="sf-cta-contact">
            <a href="mailto:contact@cloudastra.co">
              <span className="sf-cta-iwrap">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor">
                  <path d="M2 4h12v8H2z" strokeWidth="1.5" />
                  <path d="M2 4l6 5 6-5" strokeWidth="1.5" />
                </svg>
              </span>
              contact@cloudastra.co
            </a>
            <a href="/contact?ref=secureflow-CTA">
              <span className="sf-cta-iwrap">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor">
                  <rect x="2" y="3" width="12" height="10" rx="1" strokeWidth="1.5" />
                  <path d="M5 7h6M5 10h4" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </span>
              Or use our main contact form
            </a>
          </div>
        </div>

        <div className="sf-demo-card sf-reveal sf-reveal-d1" data-sf-reveal>
          <h3>Request a demo</h3>
          <p className="sf-card-sub">
            Pick a slot that works for you. One short call with a real engineer — no pitch, no slides.
          </p>
          <ul className="sf-card-points">
            <li>
              <span className="sf-card-tick">
                <svg viewBox="0 0 14 14" fill="none">
                  <path d="M2 7l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              Live walkthrough on your traffic shape
            </li>
            <li>
              <span className="sf-card-tick">
                <svg viewBox="0 0 14 14" fill="none">
                  <path d="M2 7l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              Architecture &amp; integration Q&amp;A
            </li>
            <li>
              <span className="sf-card-tick">
                <svg viewBox="0 0 14 14" fill="none">
                  <path d="M2 7l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              Sample verdicts and audit trails
            </li>
          </ul>
          <button
            type="button"
            onClick={openHubSpotBooking}
            className="sf-btn sf-btn-blue sf-btn-lg sf-card-cta"
          >
            <span>Book my demo</span>
            <svg className="sf-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 7h8m-3-3l3 3-3 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <p className="sf-card-foot">By submitting, you agree to our privacy policy.</p>
        </div>
      </div>

      {showHubSpotEmbed && (
        <div
          id='hubspot-embed-section'
          className='fixed inset-0 z-[9999] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center'
          onClick={closeHubSpotBooking}
        >
          <div
            className='relative w-full overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:mx-4 sm:max-w-4xl sm:rounded-2xl'
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeHubSpotBooking}
              aria-label='Close booking calendar'
              className='absolute top-2 right-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md text-gray-700 transition-colors hover:bg-gray-100'
            >
              <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </button>
            <div className='overflow-hidden' style={{ marginTop: '-48px' }}>
              <iframe
                src={addUtmToUrl(HUBSPOT_MEETING_URL)}
                title='Schedule a Call'
                className='w-full border-0'
                style={{ height: 'calc(85vh + 48px)', border: 'none' }}
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .sf-cta-section {
          background: var(--bg-2);
          padding: clamp(96px,11vw,150px) 0;
          position: relative;
          overflow: hidden;
        }
        .sf-cta-section::before {
          content: ''; position: absolute;
          left: 50%; top: 50%;
          width: 1100px; height: 600px;
          transform: translate(-50%,-50%);
          background: radial-gradient(closest-side,rgba(14,165,233,0.10),transparent 70%);
          pointer-events: none; z-index: 0;
        }
        .sf-cta-grid {
          display: grid; grid-template-columns: 1.05fr 1fr;
          gap: 64px; position: relative; z-index: 1;
          max-width: 1180px; margin: 0 auto;
          padding: 0 var(--gutter);
        }
        @media (max-width: 880px) {
          .sf-cta-grid { grid-template-columns: 1fr; gap: 40px; }
        }
        .sf-cta-h2 {
          margin-top: 18px;
          font-size: clamp(2rem,4vw,3rem);
          font-weight: 600; letter-spacing: -0.025em; line-height: 1.1;
        }
        .sf-cta-h2 :global(em) {
          font-style: normal; font-weight: 500;
          background: linear-gradient(120deg,var(--ca-600),var(--ca-cyan),var(--sf-600));
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        .sf-cta-sub {
          margin-top: 16px;
          font-size: clamp(0.95rem,1.18vw,1.13rem);
          color: var(--text-2); line-height: 1.65; max-width: 540px;
        }
        .sf-cta-bullets {
          list-style: none; margin-top: 32px;
          display: flex; flex-direction: column; gap: 18px;
          padding: 0;
        }
        .sf-cta-bullets li {
          display: flex; align-items: flex-start; gap: 14px;
        }
        .sf-cta-num {
          width: 30px; height: 30px;
          border-radius: 50%;
          background: var(--ca-50); border: 1px solid var(--ca-100);
          display: grid; place-items: center;
          font-family: var(--mono);
          font-size: 12px; font-weight: 600;
          color: var(--ca-700); flex-shrink: 0;
        }
        .sf-cta-txt {
          font-size: 15px; color: var(--text-2); line-height: 1.65;
        }
        .sf-cta-contact {
          margin-top: 36px;
          display: flex; flex-direction: column; gap: 12px;
          font-size: 14px; color: var(--text-2);
        }
        .sf-cta-contact a {
          display: flex; align-items: center; gap: 12px;
          text-decoration: none; color: inherit;
          transition: color 0.25s;
        }
        .sf-cta-iwrap {
          width: 38px; height: 38px; border-radius: 10px;
          background: #fff; border: 1px solid var(--line);
          display: grid; place-items: center;
          transition: all 0.25s; color: var(--text-3);
        }
        .sf-cta-contact a:hover { color: var(--ca-600); }
        .sf-cta-contact a:hover .sf-cta-iwrap {
          background: var(--ca-500); border-color: var(--ca-500); color: #fff;
        }
        .sf-cta-contact :global(svg) { width: 16px; height: 16px; }

        .sf-demo-card {
          padding: 32px; border-radius: var(--radius-lg);
          background: #fff; border: 1px solid var(--ca-200);
          box-shadow: 0 30px 80px -30px rgba(14,165,233,0.25);
          position: relative; overflow: hidden;
        }
        .sf-demo-card::before {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(60% 50% at 50% 0%,rgba(14,165,233,0.06),transparent 70%);
          pointer-events: none;
        }
        .sf-demo-card h3 {
          font-size: 20px; font-weight: 600;
          letter-spacing: -0.01em; position: relative;
        }
        .sf-card-sub {
          margin-top: 4px; font-size: 13.5px;
          color: var(--text-3); position: relative;
        }
        .sf-card-points {
          list-style: none; margin: 22px 0 0;
          padding: 0; display: flex; flex-direction: column; gap: 12px;
          position: relative;
        }
        .sf-card-points li {
          display: flex; align-items: center; gap: 12px;
          font-size: 14px; color: var(--text-2);
        }
        .sf-card-tick {
          width: 24px; height: 24px; border-radius: 50%;
          background: var(--sf-50);
          border: 1px solid var(--sf-200);
          color: var(--sf-700);
          display: grid; place-items: center; flex-shrink: 0;
        }
        .sf-card-tick :global(svg) { width: 12px; height: 12px; }
        .sf-card-cta {
          margin-top: 26px; width: 100%;
          justify-content: center; position: relative;
        }
        .sf-card-foot {
          margin-top: 14px; text-align: center;
          font-size: 11.5px; color: var(--text-4); position: relative;
        }
      `}</style>
    </section>
  )
}
